import React, { useEffect, useState } from "react";
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Select,
    MenuItem,
    Pagination,
    Chip,
    Divider,
    IconButton,
    Snackbar,
    Alert,
} from "@mui/material";

import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import { collection, addDoc, updateDoc, deleteDoc, getDocs, query, where, orderBy, doc, getDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { db, auth } from "../firebase";

const colors = {
    darkGreen: '#06362D',
    calPolyGreen: '#155332',
    vanilla: '#DEE2B1',
    gold: '#FFD500',
    goldenrod: '#D1A505'
};

const PAGE_SIZE = 10;

function Roomno() {
    const [userData, setUserData] = useState(null);
    const [propertyId, setPropertyId] = useState(null);

    const [rooms, setRooms] = useState([]);
    const [roomTypes, setRoomTypes] = useState([]);

    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);

    const [form, setForm] = useState({
        roomNo: "",
        status: "Available",
        roomTypeId: "",
        roomTypeName: "",
    });

    const [editId, setEditId] = useState(null);

    /* ---------- TOAST STATE ---------- */
    const [toast, setToast] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const showToast = (message, severity = "success") => {
        setToast({ open: true, message, severity });
    };

    /* ---------- AUTH ---------- */
    useEffect(() => {
        return onAuthStateChanged(auth, setUserData);
    }, []);

    /* ---------- PROPERTY ---------- */
    useEffect(() => {
        const loadRole = async () => {
            if (!userData) return;
            const snap = await getDoc(doc(db, "authroles", userData.uid));
            if (snap.exists()) setPropertyId(snap.data().propertyId);
        };
        loadRole();
    }, [userData]);

    /* ---------- ROOM TYPES ---------- */
    const fetchRoomTypes = async () => {
        if (!propertyId) return;

        const q = query(
            collection(db, "rooms"),
            where("propertyId", "==", propertyId),
            orderBy("roomTypeName", "asc")
        );

        const snap = await getDocs(q);
        setRoomTypes(
            snap.docs.map(d => ({
                roomTypeId: d.id,
                roomTypeName: d.data().roomTypeName,
            }))
        );
    };

    /* ---------- ROOM NUMBERS ---------- */
    const fetchRooms = async () => {
        if (!propertyId) return;

        const q = query(
            collection(db, "roominfos"),
            where("propertyId", "==", propertyId),
            orderBy("roomNo", "asc")
        );

        const snap = await getDocs(q);
        setRooms(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    };

    useEffect(() => {
        fetchRoomTypes();
        fetchRooms();
    }, [propertyId]);

    /* ---------- SAVE ---------- */
    const saveRoom = async () => {
        if (!form.roomNo || !form.roomTypeId) {
            showToast("Please fill all required fields", "warning");
            return;
        }

        try {
            const payload = {
                ...form,
                userId: userData.uid,
                propertyId,
            };

            if (editId) {
                await updateDoc(doc(db, "roominfos", editId), payload);
                showToast("Room updated successfully");
            } else {
                await addDoc(collection(db, "roominfos"), payload);
                showToast("Room added successfully");
            }

            setForm({ roomNo: "", status: "Available", roomTypeId: "", roomTypeName: "" });
            setEditId(null);
            fetchRooms();

        } catch (err) {
            showToast("Something went wrong", "error");
        }
    };

    /* ---------- EDIT ---------- */
    const editRoom = (room) => {
        setForm(room);
        setEditId(room.id);
    };

    /* ---------- DELETE ---------- */
    const deleteRoom = async (id) => {
        if (!window.confirm("Delete this room?")) return;

        try {
            await deleteDoc(doc(db, "roominfos", id));
            showToast("Room deleted successfully");
            fetchRooms();
        } catch (err) {
            showToast("Delete failed", "error");
        }
    };

    /* ---------- SEARCH + PAGINATION ---------- */
    const filtered = rooms.filter(r =>
        r.roomNo.toLowerCase().includes(search.toLowerCase()) ||
        r.roomTypeName.toLowerCase().includes(search.toLowerCase())
    );

    const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    return (
        <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: colors.darkGreen, color: colors.vanilla, p: { xs: 2, md: 4 } }}>
            <Box component="main" sx={{ flexGrow: 1, maxWidth: "1400px", margin: "0 auto" }}>

                {/* FORM */}
                <Paper sx={{ p: 3, mb: 3, borderRadius: 3, border: `1px solid ${colors.goldenrod}`, bgcolor: colors.calPolyGreen, color: colors.vanilla, boxShadow: "0 4px 20px 0 rgba(0,0,0,0.3)" }}>
                    <Typography fontWeight={600} mb={2} sx={{ color: colors.vanilla }}>
                        {editId ? "Update Room" : "Add Room"}
                    </Typography>
                    <Divider sx={{ mb: 2, borderColor: colors.goldenrod, opacity: 0.4 }} />

                    <div className="container-fluid p-0">
                        <div className="row">
                            <div className="col-sm-3 mb-3">
                                <Select
                                    size="small"
                                    value={form.roomTypeId}
                                    displayEmpty
                                    fullWidth
                                    onChange={(e) => {
                                        const rt = roomTypes.find(r => r.roomTypeId === e.target.value);
                                        setForm({
                                            ...form,
                                            roomTypeId: rt.roomTypeId,
                                            roomTypeName: rt.roomTypeName,
                                        });
                                    }}
                                    sx={{ 
                                        color: colors.vanilla, 
                                        backgroundColor: colors.darkGreen,
                                        borderRadius: "8px",
                                        '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.goldenrod }, 
                                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.gold }, 
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.gold },
                                        '& .MuiSvgIcon-root': { color: colors.vanilla }
                                    }}
                                >
                                    <MenuItem value="">Select Room Type</MenuItem>
                                    {roomTypes.map(rt => (
                                        <MenuItem key={rt.roomTypeId} value={rt.roomTypeId}>
                                            {rt.roomTypeName}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </div>

                            <div className="col-sm-3 mb-3">
                                <TextField
                                    label="Room No"
                                    size="small"
                                    fullWidth
                                    value={form.roomNo}
                                    onChange={(e) => setForm({ ...form, roomNo: e.target.value })}
                                    InputLabelProps={{ style: { color: colors.vanilla } }}
                                    InputProps={{ style: { color: colors.vanilla } }}
                                    sx={{ 
                                        "& .MuiOutlinedInput-root": { 
                                            borderRadius: "8px",
                                            backgroundColor: colors.darkGreen,
                                            "& fieldset": { borderColor: colors.goldenrod },
                                            "&:hover fieldset": { borderColor: colors.gold },
                                            "&.Mui-focused fieldset": { borderColor: colors.gold }
                                        } 
                                    }}
                                />
                            </div>

                            <div className="col-sm-3 mb-3">
                                <Select
                                    size="small"
                                    fullWidth
                                    value={form.status}
                                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                                    sx={{ 
                                        color: colors.vanilla, 
                                        backgroundColor: colors.darkGreen,
                                        borderRadius: "8px",
                                        '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.goldenrod }, 
                                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.gold }, 
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.gold },
                                        '& .MuiSvgIcon-root': { color: colors.vanilla }
                                    }}
                                >
                                    <MenuItem value="Available">Available</MenuItem>
                                    <MenuItem value="Occupied">Occupied</MenuItem>
                                </Select>
                            </div>

                            <div className="col-sm-3 mb-3">
                                <Button 
                                    variant="contained" 
                                    onClick={saveRoom}
                                    sx={{ 
                                        bgcolor: colors.goldenrod, 
                                        color: colors.darkGreen, 
                                        fontWeight: 700, 
                                        textTransform: "none", 
                                        borderRadius: "8px",
                                        height: "40px",
                                        width: "100%",
                                        '&:hover': { bgcolor: colors.gold } 
                                    }}
                                >
                                    {editId ? "Update" : "Add"}
                                </Button>
                            </div>
                        </div>
                    </div>
                </Paper>

                {/* TABLE */}
                <Paper sx={{ p: 3, borderRadius: 3, border: `1px solid ${colors.goldenrod}`, bgcolor: colors.calPolyGreen, color: colors.vanilla, boxShadow: "0 4px 20px 0 rgba(0,0,0,0.3)" }}>
                    <div className="container-fluid p-0 mb-3">
                        <div className="row">
                            <div className="col-sm-4">
                                <TextField
                                    size="small"
                                    label="Search Room No / Type"
                                    fullWidth
                                    value={search}
                                    onChange={(e) => {
                                        setSearch(e.target.value);
                                        setPage(1);
                                    }}
                                    InputLabelProps={{ style: { color: colors.vanilla } }}
                                    InputProps={{ style: { color: colors.vanilla } }}
                                    sx={{ 
                                        "& .MuiOutlinedInput-root": { 
                                            borderRadius: "8px",
                                            backgroundColor: colors.darkGreen,
                                            "& fieldset": { borderColor: colors.goldenrod },
                                            "&:hover fieldset": { borderColor: colors.gold },
                                            "&.Mui-focused fieldset": { borderColor: colors.gold }
                                        } 
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                    <Divider sx={{ my: 2, borderColor: colors.goldenrod, opacity: 0.4 }} />

                    <Table size="small">
                        <TableHead sx={{ bgcolor: colors.darkGreen }}>
                            <TableRow>
                                <TableCell sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Sr No</TableCell>
                                <TableCell sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Room No</TableCell>
                                <TableCell sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Room Type</TableCell>
                                <TableCell sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Status</TableCell>
                                <TableCell align="center" sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {paginated.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 4, color: colors.vanilla, opacity: 0.7 }}>
                                        No Records Found
                                    </TableCell>
                                </TableRow>
                            )}

                            {paginated.map((r, index) => (
                                <TableRow key={r.id} hover sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' } }}>
                                    <TableCell sx={{ color: colors.vanilla, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>
                                        {(page - 1) * PAGE_SIZE + index + 1}
                                    </TableCell>

                                    <TableCell sx={{ color: colors.gold, fontWeight: 600, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>{r.roomNo}</TableCell>
                                    <TableCell sx={{ color: colors.vanilla, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>{r.roomTypeName}</TableCell>

                                    <TableCell sx={{ borderBottom: `1px solid rgba(222,226,177,0.1)` }}>
                                        <Chip
                                            size="small"
                                            label={r.status}
                                            color={r.status === "Available" ? "success" : "error"}
                                        />
                                    </TableCell>

                                    <TableCell align="center" sx={{ borderBottom: `1px solid rgba(222,226,177,0.1)` }}>
                                        <IconButton sx={{ color: colors.gold }} onClick={() => editRoom(r)}>
                                            <EditOutlinedIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton color="error" onClick={() => deleteRoom(r.id)}>
                                            <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>

                    {totalPages > 1 && (
                        <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={(e, value) => setPage(value)}
                                color="primary"
                                size="small"
                            />
                        </Box>
                    )}
                </Paper>
            </Box>

            {/* ---------- TOAST ---------- */}
            <Snackbar
                open={toast.open}
                autoHideDuration={3000}
                onClose={() => setToast({ ...toast, open: false })}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <Alert severity={toast.severity} variant="filled">
                    {toast.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default Roomno;